import React, { useState, useEffect, useRef, useMemo } from "react";
import {
  API_BASE_URL, EVENT_TABS, EVENT_COLUMNS,
  EVENT_MESSAGES, getSubmitButtonLabel,
  getContentUpdateDescription, getSheetUrl,
  EVENT_DUPLICATE_CHECK, findDuplicates,
} from "./constants/adminConstants";
import { uploadToCloudinary } from "./uploadImage";
import AdminLogoEditor from "./AdminLogoEditor";
import "./css/adminStyles.css";

const safeParseDate = (dateStr) => {
  if (!dateStr) return new Date(NaN);
  // Check for DD/MM/YYYY format first
  const dmyMatch = dateStr.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (dmyMatch) {
    return new Date(Number(dmyMatch[3]), Number(dmyMatch[2]) - 1, Number(dmyMatch[1]));
  }
  return new Date(dateStr);
};

const formatDDMMYYYY = (dateStr) => {
  if (!dateStr) return "-";
  // If already in DD/MM/YYYY format, return as-is
  if (/^\d{1,2}\/\d{1,2}\/\d{4}$/.test(dateStr)) return dateStr;
  const d = safeParseDate(dateStr);
  if (isNaN(d.getTime())) return dateStr;
  const day = String(d.getDate()).padStart(2, "0");
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const year = d.getFullYear();
  return `${day}/${month}/${year}`;
};

const toDDMMYYYY = (dateStr) => {
  if (!dateStr) return "";
  // Already in DD/MM/YYYY format
  if (/^\d{2}\/\d{2}\/\d{4}$/.test(dateStr)) return dateStr;
  // Convert from YYYY-MM-DD
  const parts = dateStr.split("-");
  if (parts.length === 3 && parts[0].length === 4) {
    return `${parts[2]}/${parts[1]}/${parts[0]}`;
  }
  return dateStr;
};

const fromDDMMYYYY = (dateStr) => {
  if (!dateStr) return "";
  // Already in YYYY-MM-DD format
  if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) return dateStr;
  // Convert from DD/MM/YYYY (allow 1 or 2 digit day/month)
  const match = dateStr.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (match) {
    return `${match[3]}-${match[2].padStart(2, "0")}-${match[1].padStart(2, "0")}`;
  }
  return dateStr;
};

function AdminEventEditor({ user, section, onBack }) {
  const [content, setContent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState("");
  const [activeTab, setActiveTab] = useState("featuredEvents");
  const [tabOrder, setTabOrder] = useState(EVENT_TABS.map(t => t.id));
  const dragRef = useRef(null);
  const tabBarRef = useRef(null);
  useEffect(() => {
    const el = tabBarRef.current;
    if (!el) return;
    const wheelHandler = (e) => {
      if (e.deltaY !== 0) { e.preventDefault(); el.scrollLeft += e.deltaY; }
    };
    el.addEventListener("wheel", wheelHandler, { passive: false });
    return () => el.removeEventListener("wheel", wheelHandler);
  }, []);
  const [sectionInfo, setSectionInfo] = useState(null);
  const [showNoteModal, setShowNoteModal] = useState(false);
  const [noteText, setNoteText] = useState("");
  const [showSuccessOverlay, setShowSuccessOverlay] = useState(false);
  const [successTitle, setSuccessTitle] = useState("");
  const [arraySearch, setArraySearch] = useState("");
  const [searchOpen, setSearchOpen] = useState(false);
  const searchRef = useRef(null);
  useEffect(() => {
    if (searchOpen && searchRef.current) {
      searchRef.current.focus();
    }
  }, [searchOpen]);
  const [feSearch, setFeSearch] = useState("");
  const [feSearchOpen, setFeSearchOpen] = useState(false);
  const feSearchRef = useRef(null);
  useEffect(() => {
    if (feSearchOpen && feSearchRef.current) {
      feSearchRef.current.focus();
    }
  }, [feSearchOpen]);
  const [catSearch, setCatSearch] = useState("");
  const [catSearchOpen, setCatSearchOpen] = useState(false);
  const catSearchRef = useRef(null);
  useEffect(() => {
    if (catSearchOpen && catSearchRef.current) {
      catSearchRef.current.focus();
    }
  }, [catSearchOpen]);
  const [showDuplicateModal, setShowDuplicateModal] = useState(false);
  const [duplicateInfo, setDuplicateInfo] = useState([]);
  const [duplicateActionLoading, setDuplicateActionLoading] = useState(false);
  const snapshotRef = useRef(null);
  const [editingEventIdx, setEditingEventIdx] = useState(null);
  const [editingEventData, setEditingEventData] = useState(null);
  const [editingEventMode, setEditingEventMode] = useState("edit");
  const [calendarMonth, setCalendarMonth] = useState(null);
  const [showAddMonth, setShowAddMonth] = useState(false);
  const [calendarEventOverlayMonth, setCalendarEventOverlayMonth] = useState(null);
  const [addMonthYear, setAddMonthYear] = useState(new Date().getFullYear());
  const [addMonthVal, setAddMonthVal] = useState(new Date().getMonth());
  const [logoEditorOpen, setLogoEditorOpen] = useState(false);
  const [logoEditorImage, setLogoEditorImage] = useState(null);
  const [logoEditorCallback, setLogoEditorCallback] = useState(null);
  const [logoEditorRefKey, setLogoEditorRefKey] = useState(null);
  const [logoEditorAccept, setLogoEditorAccept] = useState("image/*");
  const [countriesData, setCountriesData] = useState([]);
  const [countriesSearch, setCountriesSearch] = useState("");
  const [countriesLoading, setCountriesLoading] = useState(false);

  const [calSearch, setCalSearch] = useState("");
  const [calSearchOpen, setCalSearchOpen] = useState(false);
  const calSearchRef = useRef(null);
  useEffect(() => {
    if (calSearchOpen && calSearchRef.current) {
      calSearchRef.current.focus();
    }
  }, [calSearchOpen]);
  const [countriesSearchOpen, setCountriesSearchOpen] = useState(false);
  const countrySearchRef = useRef(null);
  useEffect(() => {
    if (countriesSearchOpen && countrySearchRef.current) {
      countrySearchRef.current.focus();
    }
  }, [countriesSearchOpen]);

  const MONTHS = ["January","February","March","April","May","June","July","August","September","October","November","December"];

  useEffect(() => {
    setArraySearch("");
    setSearchOpen(false);
    setFeSearch("");
    setFeSearchOpen(false);
    setCatSearch("");
    setCatSearchOpen(false);
    setCalendarMonth(null);
    setShowAddMonth(false);
    setCalendarEventOverlayMonth(null);

    setCalSearch("");
    setCalSearchOpen(false);
    setCountriesSearchOpen(false);
  }, [activeTab]);

  useEffect(() => {
    loadContent();
  }, []);

  const loadContent = async () => {
    const token = sessionStorage.getItem("tvc_token");
    try {
      const res = await fetch(`${API_BASE_URL}/content/${section.key}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (data && data.section) {
        const c = data.section.content;
        setContent(c);
        snapshotRef.current = JSON.stringify(c);
        setSectionInfo({ version: data.section.version, updatedAt: data.section.updatedAt });
      } else {
        setMessage(EVENT_MESSAGES.apiError);
      }
    } catch (err) {
      setMessage(EVENT_MESSAGES.connectionError);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenSheet = () => {
    window.open(getSheetUrl(section.key), "_blank", "noopener,noreferrer");
  };

  const handlePushToSheet = async () => {
    const token = sessionStorage.getItem("tvc_token");
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE_URL}/content/${section.key}/push-sheet`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ content }),
      });
      if (!res.ok) {
        const text = await res.text();
        setMessage(`Push failed (${res.status}): ${text.substring(0, 200)}`);
        return;
      }
      const data = await res.json();
      setMessage(data.success ? "Pushed to Google Sheet successfully!" : data.error || "Push failed.");
    } catch (err) {
      setMessage("Push error: " + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handlePullFromSheet = async () => {
    const token = sessionStorage.getItem("tvc_token");
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE_URL}/content/${section.key}/pull-sheet`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (data.success) {
        setContent(data.content);
        setMessage("Pulled from Google Sheet. Review changes and click Publish to go live.");
      } else {
        setMessage(data.error || "Pull failed.");
      }
    } catch (err) {
      setMessage("Pull error: " + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleArrayChange = (field, index, key, value) => {
    setContent((prev) => {
      const updated = { ...prev };
      const arr = [...(updated[field] || [])];
      if (arr[index]) {
        arr[index] = { ...arr[index], [key]: value };
      }
      updated[field] = arr;
      return updated;
    });
  };

  const handleAddRowBefore = (field, index) => {
    setContent((prev) => {
      const updated = { ...prev };
      const arr = [...(updated[field] || [])];
      const emptyRow = {};
      if (arr.length > 0) {
        for (const key of Object.keys(arr[0])) {
          emptyRow[key] = "";
        }
      } else {
        const cols = EVENT_COLUMNS[field];
        if (cols) {
          for (const col of cols) {
            emptyRow[col.key] = "";
          }
        }
      }
      arr.splice(index, 0, emptyRow);
      updated[field] = arr;
      return updated;
    });
  };

  const handleAddRowAfter = (field, index) => {
    setContent((prev) => {
      const updated = { ...prev };
      const arr = [...(updated[field] || [])];
      const emptyRow = {};
      if (arr.length > 0) {
        for (const key of Object.keys(arr[0])) {
          emptyRow[key] = "";
        }
      } else {
        const cols = EVENT_COLUMNS[field];
        if (cols) {
          for (const col of cols) {
            emptyRow[col.key] = "";
          }
        }
      }
      arr.splice(index + 1, 0, emptyRow);
      updated[field] = arr;
      return updated;
    });
  };

  const handleDeleteRow = (field, index) => {
    setContent((prev) => {
      const updated = { ...prev };
      updated[field] = (updated[field] || []).filter((_, i) => i !== index);
      return updated;
    });
  };

  const handleMoveRowUp = (field, index) => {
    if (index === 0) return;
    setContent((prev) => {
      const updated = { ...prev };
      const arr = [...(updated[field] || [])];
      [arr[index - 1], arr[index]] = [arr[index], arr[index - 1]];
      updated[field] = arr;
      return updated;
    });
  };

  const handleMoveRowDown = (field, index) => {
    setContent((prev) => {
      const updated = { ...prev };
      const arr = [...(updated[field] || [])];
      if (index >= arr.length - 1) return prev;
      [arr[index], arr[index + 1]] = [arr[index + 1], arr[index]];
      updated[field] = arr;
      return updated;
    });
  };

  const handleFieldChange = (field, value) => {
    setContent((prev) => {
      if (field.startsWith("settings.")) {
        const parts = field.split(".");
        return { ...prev, settings: { ...prev.settings, [parts[1]]: value } };
      }
      return { ...prev, [field]: value };
    });
  };

  const handleSubmit = async () => {
    const dupes = findDuplicates(content, EVENT_DUPLICATE_CHECK);
    if (dupes.length > 0) {
      setDuplicateInfo(dupes);
      setShowDuplicateModal(true);
      return;
    }
    const role = user?.role || "Staff";
    if (role !== "Admin") {
      setNoteText("");
      setShowNoteModal(true);
      return;
    }
    await doSubmit();
  };

  const handleConfirmSubmit = async () => {
    if (!noteText.trim()) return;
    setShowNoteModal(false);
    await doSubmit(noteText.trim());
  };

  const handleContinueAnyway = async () => {
    setShowDuplicateModal(false);
    const role = user?.role || "Staff";
    if (role !== "Admin") {
      setNoteText("");
      setShowNoteModal(true);
      return;
    }
    await doSubmit();
  };

  const handleRemoveDuplicates = async () => {
    setDuplicateActionLoading(true);
    const cleanContent = JSON.parse(JSON.stringify(content));
    const sorted = [...duplicateInfo].sort((a, b) => {
      if (a.sectionIndex !== undefined && b.sectionIndex !== undefined) {
        if (a.sectionIndex !== b.sectionIndex) return b.sectionIndex - a.sectionIndex;
      }
      return b.duplicateIndex - a.duplicateIndex;
    });
    for (const dup of sorted) {
      const arr = [...(cleanContent[dup.field] || [])];
      if (arr[dup.duplicateIndex]) {
        arr.splice(dup.duplicateIndex, 1);
        cleanContent[dup.field] = arr;
      }
    }
    setContent(cleanContent);
    setShowDuplicateModal(false);
    setDuplicateActionLoading(false);
    snapshotRef.current = JSON.stringify(cleanContent);
    const role = user?.role || "Staff";
    if (role !== "Admin") {
      setNoteText("");
      setShowNoteModal(true);
      return;
    }
    await doSubmit("", cleanContent);
  };

  const doSubmit = async (submitterNote, overrideContent) => {
    setMessage("");
    setSaving(true);
    const token = sessionStorage.getItem("tvc_token");
    const submitPayload = overrideContent || { ...content };
    const estimateSize = new Blob([JSON.stringify(submitPayload)]).size;
    if (estimateSize > 4 * 1024 * 1024) {
      setMessage(`Payload too large (${(estimateSize / 1024 / 1024).toFixed(1)} MB). Reduce image sizes before submitting.`);
      setSaving(false);
      return;
    }
    try {
      const res = await fetch(`${API_BASE_URL}/content/submit-change`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify({
          description: getContentUpdateDescription(section.label),
          changes: {
            type: "content_update",
            sectionKey: section.key,
            content: submitPayload,
            ...(submitterNote ? { submitterNote } : {}),
          },
          sectionKey: section.key,
        }),
      });
      const text = await res.text();
      let data;
      try { data = JSON.parse(text); } catch { data = { error: text }; }
      if (res.ok) {
        snapshotRef.current = JSON.stringify(content);
        const role = user?.role || "Staff";
        setSuccessTitle(role === "Admin" ? "Changes Published" : "Request made");
        setShowSuccessOverlay(true);
      } else {
        setMessage(data.error || `Server error (${res.status})`);
      }
    } catch (err) {
      setMessage(`Network error: ${err.message}`);
    } finally {
      setSaving(false);
    }
  };

  const handleImageUpload = async (field, index) => {
    const input = document.createElement("input");
    input.type = "file";
    input.accept = "image/*";
    input.onchange = async (e) => {
      const file = e.target.files?.[0];
      if (!file) return;
      const reader = new FileReader();
      reader.onloadend = async () => {
        const dataUrl = reader.result;
        try {
          const url = await uploadToCloudinary(dataUrl, file.name);
          handleArrayChange(field, index, "imageUrl", url);
        } catch (err) {
          setMessage("Upload failed: " + err.message);
        }
      };
      reader.readAsDataURL(file);
    };
    input.click();
  };

  const calendarGroups = useMemo(() => {
    const groups = {};
    (content?.featuredEvents || []).forEach((ev, idx) => {
      if (ev.date) {
        const d = safeParseDate(ev.date);
        if (!isNaN(d.getTime())) {
          const year = d.getFullYear();
          const m = d.getMonth();
          const key = `${year}-${m}`;
          if (!groups[key]) {
            groups[key] = { year, month: m, monthLabel: MONTHS[m], events: [] };
          }
          groups[key].events.push({ ...ev, _idx: idx });
        }
      }
    });
    return Object.values(groups).sort((a, b) => a.year - b.year || a.month - b.month);
  }, [content?.featuredEvents, MONTHS]);

  const closeLogoEditor = () => {
    setLogoEditorOpen(false);
    setLogoEditorImage(null);
    setLogoEditorCallback(null);
    setLogoEditorRefKey(null);
    setLogoEditorAccept("image/*");
  };

  const handleLogoEditorSave = async (newUrl) => {
    const oldUrl = logoEditorImage;
    let finalUrl = newUrl || oldUrl;
    if (!finalUrl) { closeLogoEditor(); return; }
    if (finalUrl.startsWith("data:")) {
      try {
        finalUrl = await uploadToCloudinary(finalUrl, "event-image");
      } catch (err) {
        finalUrl = oldUrl;
      }
    }
    setEditingEventData(prev => prev ? { ...prev, imageUrl: finalUrl } : prev);
    if (logoEditorRefKey && oldUrl && oldUrl !== finalUrl) {
      setContent(prev => {
        const history = { ...(prev.imageHistory || {}) };
        const existing = history[logoEditorRefKey] || [];
        history[logoEditorRefKey] = [...existing.filter(u => u !== oldUrl), oldUrl];
        return { ...prev, imageHistory: history };
      });
    }
    closeLogoEditor();
  };

  const handleLogoEditorClose = () => {
    setLogoEditorOpen(false);
    setLogoEditorImage(null);
    setLogoEditorCallback(null);
    setLogoEditorRefKey(null);
    setLogoEditorAccept("image/*");
  };

  const handleLogoEditorDeleteHistory = (index) => {
    if (!logoEditorRefKey) return;
    setContent(prev => {
      const history = { ...(prev.imageHistory || {}) };
      const bin = { ...(prev.imageBin || {}) };
      const arr = history[logoEditorRefKey] || [];
      const removed = arr[index];
      if (!removed) return prev;
      history[logoEditorRefKey] = arr.filter((_, i) => i !== index);
      bin[logoEditorRefKey] = [...(bin[logoEditorRefKey] || []), removed];
      return { ...prev, imageHistory: history, imageBin: bin };
    });
  };

  const handleLogoEditorRestoreBin = (index) => {
    if (!logoEditorRefKey) return;
    setContent(prev => {
      const bin = { ...(prev.imageBin || {}) };
      const history = { ...(prev.imageHistory || {}) };
      const arr = bin[logoEditorRefKey] || [];
      const restored = arr[index];
      if (!restored) return prev;
      bin[logoEditorRefKey] = arr.filter((_, i) => i !== index);
      history[logoEditorRefKey] = [...(history[logoEditorRefKey] || []), restored];
      return { ...prev, imageBin: bin, imageHistory: history };
    });
  };

  const handleLogoEditorPermanentDelete = (index) => {
    if (!logoEditorRefKey) return;
    setContent(prev => {
      const bin = { ...(prev.imageBin || {}) };
      const arr = bin[logoEditorRefKey] || [];
      if (!arr[index]) return prev;
      bin[logoEditorRefKey] = arr.filter((_, i) => i !== index);
      return { ...prev, imageBin: bin };
    });
  };

  const countriesFetchedRef = useRef(false);
  useEffect(() => {
    if (activeTab === "countries" && !countriesFetchedRef.current) {
      countriesFetchedRef.current = true;
      setCountriesLoading(true);
      const token = sessionStorage.getItem("tvc_token");
      fetch(`${API_BASE_URL}/countries`, {
        headers: { Authorization: `Bearer ${token}` },
      })
        .then(r => r.json())
        .then(data => {
          if (data.countries) setCountriesData(data.countries);
        })
        .catch(() => {})
        .finally(() => setCountriesLoading(false));
    }
  }, [activeTab]);

  const lastLocationParts = useRef("");
  useEffect(() => {
    if (!editingEventData) return;
    const parts = [editingEventData.city, editingEventData.country].filter(Boolean);
    const key = parts.join("||");
    if (parts.length > 0 && lastLocationParts.current !== key) {
      lastLocationParts.current = key;
      const autoLocation = parts.join(", ");
      setEditingEventData(prev => prev ? { ...prev, location: autoLocation } : prev);
    }
  }, [editingEventData?.city, editingEventData?.country]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleCalendarEdit = (year, month) => {
    setCalendarMonth(`${year}-${month}`);
  };

  const handleCloseCalendarEdit = () => {
    setCalendarMonth(null);
  };

  const handleAddEventToMonth = (year, month) => {
    const dateStr = `${year}-${String(month + 1).padStart(2, "0")}-01`;
    setContent((prev) => {
      const updated = { ...prev };
      const arr = [...(updated.featuredEvents || [])];
      const emptyRow = {};
      const cols = EVENT_COLUMNS.featuredEvents;
      for (const col of cols) {
        emptyRow[col.key] = "";
      }
      emptyRow.date = dateStr;
      arr.push(emptyRow);
      updated.featuredEvents = arr;
      return updated;
    });
  };

  const handleAddMonth = () => {
    const year = addMonthYear;
    const month = addMonthVal;
    const key = `${year}-${month}`;
    const existing = calendarGroups.find((g) => g.year === year && g.month === month);
    setShowAddMonth(false);
    if (!existing) {
      const dateStr = `${year}-${String(month + 1).padStart(2, "0")}-01`;
      setContent((prev) => {
        const updated = { ...prev };
        const arr = [...(updated.featuredEvents || [])];
        const emptyRow = {};
        const cols = EVENT_COLUMNS.featuredEvents;
        for (const col of cols) {
          emptyRow[col.key] = "";
        }
        emptyRow.date = dateStr;
        arr.push(emptyRow);
        updated.featuredEvents = arr;
        return updated;
      });
    }
    setCalendarMonth(key);
  };

  const openEventOverlay = (idx) => {
    const items = content?.featuredEvents || [];
    if (idx >= 0 && idx < items.length) {
      const item = { ...items[idx] };
      setEditingEventData(item);
      setEditingEventIdx(idx);
      setEditingEventMode("edit");
    }
  };

  const openNewEventOverlay = (insertAt) => {
    const emptyRow = {};
    for (const col of EVENT_COLUMNS.featuredEvents) {
      emptyRow[col.key] = "";
    }
    emptyRow.visaRequired = "";
    setEditingEventData(emptyRow);
    setEditingEventIdx(insertAt);
    setEditingEventMode("new");
  };

  const openCalendarNewEventOverlay = (year, month) => {
    const dateStr = `${year}-${String(month + 1).padStart(2, "0")}-01`;
    const len = content?.featuredEvents?.length || 0;
    const emptyRow = {};
    for (const col of EVENT_COLUMNS.featuredEvents) {
      emptyRow[col.key] = "";
    }
    emptyRow.visaRequired = "";
    emptyRow.date = dateStr;
    setEditingEventData(emptyRow);
    setEditingEventIdx(len);
    setEditingEventMode("new");
    setCalendarEventOverlayMonth(`${year}-${month}`);
  };

  const handleSaveEventOverlay = () => {
    if (!editingEventData) return;
    setContent((prev) => {
      const updated = { ...prev };
      const arr = [...(updated.featuredEvents || [])];
      if (editingEventMode === "new") {
        arr.splice(editingEventIdx, 0, { ...editingEventData });
      } else {
        arr[editingEventIdx] = { ...editingEventData };
      }
      updated.featuredEvents = arr;
      if (calendarEventOverlayMonth) {
        const [calY, calM] = calendarEventOverlayMonth.split("-").map(Number);
        const calData = { ...(updated.calendarData || {}) };
        const monthEvents = arr.filter((ev) => {
          if (!ev.date) return false;
          const d = new Date(ev.date);
          return !isNaN(d.getTime()) && d.getFullYear() === calY && d.getMonth() === calM;
        });
        calData[calendarEventOverlayMonth] = monthEvents;
        updated.calendarData = calData;
      }
      return updated;
    });
    setEditingEventIdx(null);
    setEditingEventData(null);
    setCalendarEventOverlayMonth(null);
  };

  const handleOverlayFieldChange = (key, value) => {
    setEditingEventData((prev) => (prev ? { ...prev, [key]: value } : prev));
  };

  const renderArrayEditor = (field, columns, searchVal, searchOpenState, setSearchVal, setSearchOpenState, searchRefField) => {
    const items = content?.[field] || [];
    return (
      <div className="AdminCardMb">
        <div className="AdminSectionHeader">
          <h4 className="AdminSectionTitle">
            {field.replace(/([A-Z])/g, " $1").trim()}
          </h4>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
            <div className={`AdminPillSearchWrap ${searchOpenState ? 'is-open' : ''}`}>
              <span className="AdminPillSearchIcon">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
                </svg>
              </span>
              <input
                ref={searchRefField}
                type="text"
                className="AdminPillSearch"
                placeholder="Search..."
                value={searchVal}
                onChange={(e) => setSearchVal(e.target.value)}
                onBlur={() => { setSearchVal(""); setTimeout(() => setSearchOpenState(false), 200) }}
              />
              {!searchOpenState && (
                <button className="AdminPillSearchOverlay" onClick={() => setSearchOpenState(true)} type="button" aria-label="Search" />
              )}
            </div>
            <button
              className="AdminButton AdminButtonSmall AdminButtonSecondary AdminAddRowBtn"
              onClick={() => handleAddRowAfter(field, items.length - 1)}
            >
              + Add Row
            </button>
          </div>
        </div>
        <div className="AdminTableScroll">
          <table className="AdminTable AdminTableEditor">
            <thead>
              <tr>
                <th className="AdminThOrder">Order</th>
                {columns.map((col) => (
                  <th key={col.key}>{col.label}</th>
                ))}
                <th className="AdminThActions">Actions</th>
              </tr>
            </thead>
            <tbody>
              {items
                .map((item, idx) => ({ item, idx }))
                .filter(({ item }) => {
                  if (!searchVal.trim()) return true;
                  return columns.some((col) =>
                    String(item[col.key] || "").toLowerCase().includes(searchVal.trim().toLowerCase())
                  );
                })
                .map(({ item, idx }) => (
                <tr
                  key={idx}
                  draggable="true"
                  onDragStart={(e) => {
                    dragRef.current = { type: "array", field, fromIndex: idx };
                    e.dataTransfer.effectAllowed = "move";
                    e.dataTransfer.setData("text/plain", JSON.stringify(dragRef.current));
                    e.currentTarget.style.opacity = "0.4";
                  }}
                  onDragOver={(e) => {
                    e.preventDefault();
                    e.dataTransfer.dropEffect = "move";
                    e.currentTarget.style.outline = "2px dashed #3b82f6";
                    e.currentTarget.style.outlineOffset = "-2px";
                  }}
                  onDragLeave={(e) => { e.currentTarget.style.outline = ""; }}
                  onDrop={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    e.currentTarget.style.opacity = "";
                    e.currentTarget.style.outline = "";
                    if (!dragRef.current || dragRef.current.type !== "array") { dragRef.current = null; return; }
                    const { field: fromField, fromIndex } = dragRef.current;
                    if (fromField !== field || fromIndex === idx) { dragRef.current = null; return; }
                    setContent((prev) => {
                      const updated = { ...prev };
                      const arr = [...(updated[field] || [])];
                      const [moved] = arr.splice(fromIndex, 1);
                      arr.splice(idx, 0, moved);
                      updated[field] = arr;
                      return updated;
                    });
                    dragRef.current = null;
                  }}
                  onDragEnd={(e) => {
                    e.currentTarget.style.opacity = "";
                    e.currentTarget.style.outline = "";
                    dragRef.current = null;
                  }}
                >
                  <td>
                    <div className="AdminOrderBtns">
                      <button className="AdminMoveBtn" onClick={() => handleMoveRowUp(field, idx)} disabled={idx === 0} title="Move up">&#8593;</button>
                      <button className="AdminMoveBtn" onClick={() => handleMoveRowDown(field, idx)} disabled={idx === items.length - 1} title="Move down">&#8595;</button>
                    </div>
                  </td>
                  {columns.map((col) => (
                    <td key={col.key}>
                      {col.type === "textarea" ? (
                        <textarea
                          className="AdminInput AdminTableInput"
                          style={{ minHeight: 60, resize: "vertical" }}
                          value={item[col.key] || ""}
                          onChange={(e) => handleArrayChange(field, idx, col.key, e.target.value)}
                          placeholder={col.label}
                        />
                      ) : col.type === "imageUpload" ? (
                        <div className="AdminIconCell">
                          {item[col.key] && (
                            <img
                              src={item[col.key]}
                              alt=""
                              style={{ width: 32, height: 32, borderRadius: 4, objectFit: "cover", flexShrink: 0 }}
                              onError={(e) => { e.target.style.display = "none" }}
                            />
                          )}
                          <input
                            className="AdminInput AdminTableInput"
                            value={item[col.key] || ""}
                            onChange={(e) => handleArrayChange(field, idx, col.key, e.target.value)}
                            placeholder="Image URL"
                          />
                          <button className="AdminIconUploadBtn" title="Upload image" onClick={() => handleImageUpload(field, idx)}>
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                              <path d="M9 16h6v-6h4l-7-7-7 7h4zm-4 2h14v2H5z"/>
                            </svg>
                          </button>
                        </div>
                      ) : col.type === "select" ? (
                        <select
                          className="AdminInput AdminTableInput"
                          value={item[col.key] || ""}
                          onChange={(e) => handleArrayChange(field, idx, col.key, e.target.value)}
                        >
                          {col.options.map((opt) => (
                            <option key={opt} value={opt}>{opt}</option>
                          ))}
                        </select>
                      ) : (
                        <input
                          className="AdminInput AdminTableInput"
                          value={item[col.key] || ""}
                          onChange={(e) => handleArrayChange(field, idx, col.key, e.target.value)}
                          placeholder={col.label}
                        />
                      )}
                    </td>
                  ))}
                  <td>
                    <div className="AdminActionBtns">
                      <button className="AdminIconBtn AdminIconBtnBlue" onClick={() => handleAddRowBefore(field, idx)} title="Add row before">&#9650;+</button>
                      <button className="AdminIconBtn AdminIconBtnBlue" onClick={() => handleAddRowAfter(field, idx)} title="Add row after">&#9660;+</button>
                      <button className="AdminIconBtn AdminIconBtnRed" onClick={() => handleDeleteRow(field, idx)} title="Delete row">&#10005;</button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {items.length === 0 && (
          <div className="AdminEmptyState">{EVENT_MESSAGES.emptyArray}</div>
        )}
        {items.length > 0 && searchVal.trim() && !items.some((item) =>
          columns.some((col) =>
            String(item[col.key] || "").toLowerCase().includes(searchVal.trim().toLowerCase())
          )
        ) && (
          <div className="AdminEmptyState">No results found for "{searchVal}"</div>
        )}
      </div>
    );
  };

  if (loading) {
    return <div className="AdminEmptyState">{EVENT_MESSAGES.loading}</div>;
  }

  if (!content) {
    return (
      <div>
        <button className="AdminButton AdminButtonSmall AdminButtonSecondary AdminBackBtn" onClick={onBack}>
          &larr; Back to Content Manager
        </button>
        <div className="AdminEmptyState">{EVENT_MESSAGES.noContent}</div>
      </div>
    );
  }

  const isDirty = JSON.stringify(content) !== snapshotRef.current;
  const orderedTabs = [...EVENT_TABS].sort((a, b) => tabOrder.indexOf(a.id) - tabOrder.indexOf(b.id));

  return (
    <div className="AdminCard">
      <div className="AdminCardHeader">
        <h3 className="AdminFooterH3">
          Edit Event Content
          {(() => {
            const k = activeTab === "calendar" ? "featuredEvents" : activeTab;
            return k !== "data" && Array.isArray(content?.[k]) ? <span className="AdminCountBadge">{content[k].length}</span> : null;
          })()}
        </h3>
        <div style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: "8px" }}>
          <button className="AdminButton AdminButtonSmall AdminSubmitBtn" onClick={handleSubmit} disabled={!isDirty || saving} style={{ whiteSpace: "nowrap" }}>
            {saving ? "Publishing..." : getSubmitButtonLabel(user?.role)}
          </button>
          <button className="AdminButton AdminButtonSmall AdminSheetBtn" onClick={handleOpenSheet}>
            Google Sheet
          </button>
          <button className="AdminSheetIconBtn" onClick={handlePushToSheet} disabled={loading} title="Push to Sheet">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
              <polyline points="17 8 12 3 7 8"/>
              <line x1="12" y1="3" x2="12" y2="15"/>
            </svg>
          </button>
          <button className="AdminSheetIconBtn" onClick={handlePullFromSheet} disabled={loading} title="Pull from Sheet">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
              <polyline points="7 10 12 15 17 10"/>
              <line x1="12" y1="15" x2="12" y2="3"/>
            </svg>
          </button>
        </div>
      </div>

      {message && (
        <p className={`AdminMsgBox ${message.includes("success") ? "AdminSuccess" : "AdminError"}`}>
          {message}
        </p>
      )}

      <div className="AdminTabBar" ref={tabBarRef} style={{ display: "flex", gap: 0, borderBottom: "2px solid #e2e8f0", marginBottom: 16 }}>
        {orderedTabs.map((tab) => (
          <button
            key={tab.id}
            draggable
            onDragStart={(e) => { dragRef.current = tab.id; e.target.style.opacity = '0.5'; }}
            onDragOver={(e) => e.preventDefault()}
            onDrop={(e) => {
              e.preventDefault();
              const fromId = dragRef.current;
              if (!fromId || fromId === tab.id) return;
              const newOrder = [...tabOrder];
              const fromIdx = newOrder.indexOf(fromId);
              const toIdx = newOrder.indexOf(tab.id);
              newOrder.splice(fromIdx, 1);
              newOrder.splice(toIdx, 0, fromId);
              setTabOrder(newOrder);
            }}
            onDragEnd={(e) => { e.target.style.opacity = '1'; dragRef.current = null; }}
            className={`AdminTabBtn ${activeTab === tab.id ? "AdminTabBtnActive" : ""}`}
            onClick={() => setActiveTab(tab.id)}
            style={{
              padding: "10px 20px", border: "none", background: activeTab === tab.id ? "#1B2A4A" : "transparent",
              color: activeTab === tab.id ? "#fff" : "#64748b", cursor: "grab", fontSize: 14, fontWeight: 600,
              borderRadius: "8px 8px 0 0", transition: "all 0.2s",
            }}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {activeTab === "data" && (
        <div className="AdminCard">
          <div className="AdminDataHeader">
            <h4 className="AdminSectionTitle">Data</h4>
          </div>
          <div className="AdminDataInfo">
            <div>
              <span className="AdminDataLabel">Last Updated</span>
              <p className="AdminDataValue">
                {sectionInfo?.updatedAt ? new Date(sectionInfo.updatedAt).toLocaleString() : "Not published"}
              </p>
            </div>
            <div>
              <span className="AdminDataLabel">Section Key</span>
              <p className="AdminDataValue">{section?.key || "event"}</p>
            </div>
          </div>
          <textarea
            className="AdminJsonEditor"
            value={JSON.stringify(content, null, 2)}
            onChange={(e) => {
              try {
                const parsed = JSON.parse(e.target.value);
                setContent(parsed);
                setMessage("");
              } catch {
                setMessage(EVENT_MESSAGES.invalidJson);
              }
            }}
          />
        </div>
      )}

      {activeTab === "featuredEvents" && (
        <div>
          <div className="AdminCardMb">
            <div className="AdminSectionHeader">
              <h4 className="AdminSectionTitle">Event Settings</h4>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, padding: '0 0 16px 0' }}>
              <div className="AdminFormGroup">
                <label className="AdminLabel">Hero Title</label>
                <input
                  className="AdminInput"
                  value={content.heroTitle || ""}
                  onChange={(e) => handleFieldChange("heroTitle", e.target.value)}
                  placeholder="Hero title"
                />
              </div>
              <div className="AdminFormGroup">
                <label className="AdminLabel">Hero Subtitle</label>
                <input
                  className="AdminInput"
                  value={content.heroSubtitle || ""}
                  onChange={(e) => handleFieldChange("heroSubtitle", e.target.value)}
                  placeholder="Hero subtitle"
                />
              </div>
            </div>
          </div>
          <div className="AdminCardMb">
            <div className="AdminSectionHeader">
              <h4 className="AdminSectionTitle">Featured Events ({content?.featuredEvents?.length || 0})</h4>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
                <div className={`AdminPillSearchWrap ${feSearchOpen ? 'is-open' : ''}`}>
                  <span className="AdminPillSearchIcon">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                      <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
                    </svg>
                  </span>
                  <input
                    ref={feSearchRef} type="text" className="AdminPillSearch" placeholder="Search..."
                    value={feSearch} onChange={(e) => setFeSearch(e.target.value)}
                    onBlur={() => { setFeSearch(""); setTimeout(() => setFeSearchOpen(false), 200) }}
                  />
                  {!feSearchOpen && (
                    <button className="AdminPillSearchOverlay" onClick={() => setFeSearchOpen(true)} type="button" aria-label="Search" />
                  )}
                </div>
                <button className="AdminButton AdminButtonSmall AdminButtonSecondary AdminAddRowBtn" onClick={() => {
                  const len = content?.featuredEvents?.length || 0;
                  openNewEventOverlay(len);
                }}>+ Add Row</button>
              </div>
            </div>
            <div className="AdminTableScroll">
              <table className="AdminTable AdminTableEditor">
                <thead>
                  <tr>
                    <th className="AdminThOrder">Order</th>
                    <th>Title</th>
                    <th>Date</th>
                    <th>Category</th>
                    <th>Country</th>
                    <th>City</th>
                    <th>Visa Status</th>
                    <th>Image</th>
                    <th className="AdminThActions">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {(content?.featuredEvents || [])
                    .map((item, idx) => ({ item, idx }))
                    .filter(({ item }) => {
                      if (!feSearch.trim()) return true;
                      return Object.values(item).some((v) => String(v || "").toLowerCase().includes(feSearch.trim().toLowerCase()));
                    })
                    .map(({ item, idx }) => (
                    <tr key={idx}>
                      <td>
                        <div className="AdminOrderBtns">
                          <button className="AdminMoveBtn" onClick={() => handleMoveRowUp("featuredEvents", idx)} disabled={idx === 0} title="Move up">&#8593;</button>
                          <button className="AdminMoveBtn" onClick={() => handleMoveRowDown("featuredEvents", idx)} disabled={idx === (content?.featuredEvents?.length || 0) - 1} title="Move down">&#8595;</button>
                          <button className="AdminIconBtn AdminIconBtnBlue" title="Edit" onClick={() => openEventOverlay(idx)}>&#9998;</button>
                        </div>
                      </td>
                      <td style={{ maxWidth: 160, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{item.title || "-"}</td>
                      <td style={{ whiteSpace: "nowrap" }}>{formatDDMMYYYY(item.date)}</td>
                      <td>{item.category || "-"}</td>
                      <td>{item.country || "-"}</td>
                      <td>{item.city || "-"}</td>
                      <td>
                        {item.visaRequired ? (
                          <span style={{
                            display: "inline-block",
                            padding: "2px 8px",
                            borderRadius: 10,
                            fontSize: 10,
                            fontWeight: 700,
                            background: item.visaRequired === "Visa Required" ? "rgba(240, 165, 0, 0.18)" : "rgba(60, 201, 122, 0.16)",
                            color: item.visaRequired === "Visa Required" ? "#f0a500" : "#3cc97a",
                          }}>{item.visaRequired}</span>
                        ) : "-"}
                      </td>
                      <td>{item.imageUrl ? <img src={item.imageUrl} alt="" style={{ width: 36, height: 24, borderRadius: 4, objectFit: "cover" }} /> : "-"}</td>
                      <td>
                        <div className="AdminActionBtns">
                          <button className="AdminIconBtn AdminIconBtnBlue" title="Add above" onClick={() => openNewEventOverlay(idx)}>&#9650;+</button>
                          <button className="AdminIconBtn AdminIconBtnBlue" title="Add below" onClick={() => openNewEventOverlay(idx + 1)}>&#9660;+</button>
                          <button className="AdminIconBtn AdminIconBtnRed" title="Delete" onClick={() => handleDeleteRow("featuredEvents", idx)}>&#10005;</button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {(content?.featuredEvents || []).length === 0 && (
              <div className="AdminEmptyState">{EVENT_MESSAGES.emptyArray}</div>
            )}
          </div>
        </div>
      )}

      {/* Event Edit Overlay */}
      {editingEventIdx !== null && editingEventData && (
        <div className="AdminModal" style={{ zIndex: 1050 }}>
          <div className="AdminModalContent" style={{ maxWidth: 700, maxHeight: "90vh", overflowY: "auto" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
              <h3 className="AdminModalTitle" style={{ margin: 0 }}>{editingEventMode === "new" ? "Add Event" : "Edit Event"}</h3>
              <button className="AdminCloseBtn" onClick={() => { setEditingEventIdx(null); setEditingEventData(null); setCalendarEventOverlayMonth(null); }}>&times;</button>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
              {EVENT_COLUMNS.featuredEvents.filter(col => col.key !== "description").map((col) => (
                <div key={col.key} className="AdminFormGroup" style={col.key === "imageUrl" ? { gridColumn: "1 / -1" } : {}}>
                  <label className="AdminLabel">{col.label}</label>
                  {col.key === "visaRequired" ? (
                    <select
                      className="AdminInput"
                      value={editingEventData[col.key] || ""}
                      onChange={(e) => handleOverlayFieldChange(col.key, e.target.value)}
                    >
                      <option value="">Select...</option>
                      <option value="Visa Required">Visa Required</option>
                      <option value="Visa Free">Visa Free</option>
                    </select>
                  ) : col.key === "country" ? (
                    <div style={{ position: "relative" }}>
                      <input
                        className="AdminInput"
                        value={editingEventData[col.key] || ""}
                        onChange={(e) => handleOverlayFieldChange(col.key, e.target.value)}
                        placeholder="Search country..."
                        list="country-select-list"
                      />
                      <datalist id="country-select-list">
                        {(content?.countries || countriesData).map((c) => (
                          <option key={c.code || c.name} value={c.name} />
                        ))}
                      </datalist>
                    </div>
                  ) : col.key === "category" ? (
                    <div style={{ position: "relative" }}>
                      <input
                        className="AdminInput"
                        value={editingEventData[col.key] || ""}
                        onChange={(e) => {
                          handleOverlayFieldChange(col.key, e.target.value);
                          const val = e.target.value.trim();
                          if (val && !(content?.categories || []).some(c => c.label?.toLowerCase() === val.toLowerCase())) {
                            const catId = val.toLowerCase().replace(/\s+/g, "-");
                            setContent(prev => ({
                              ...prev,
                              categories: [...(prev.categories || []).filter(c => c.id !== "all"), { id: catId, label: val }],
                            }));
                          }
                        }}
                        placeholder="Type or select a category"
                        list="category-select-list"
                      />
                      <datalist id="category-select-list">
                        {(content?.categories || []).filter(c => c.id !== "all").map((cat) => (
                          <option key={cat.id} value={cat.label} />
                        ))}
                      </datalist>
                    </div>
                  ) : col.type === "textarea" ? (
                    <textarea
                      className="AdminInput" style={{ minHeight: 80, resize: "vertical", width: "100%", boxSizing: "border-box" }}
                      value={editingEventData[col.key] || ""}
                      onChange={(e) => handleOverlayFieldChange(col.key, e.target.value)}
                      placeholder={col.label}
                    />
                  ) : col.type === "imageUpload" ? (
                    <div>
                      <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                        {editingEventData[col.key] && (
                          <img src={editingEventData[col.key]} alt=""
                            style={{ width: 72, height: 48, borderRadius: 6, objectFit: "cover", flexShrink: 0, cursor: "pointer" }}
                            onClick={() => {
                              setLogoEditorImage(editingEventData[col.key]);
                              setLogoEditorRefKey("featured-event-image");
                              setLogoEditorAccept("image/*");
                              setLogoEditorOpen(true);
                            }}
                          />
                        )}
                        <input
                          className="AdminInput" style={{ flex: 1 }}
                          value={editingEventData[col.key] || ""}
                          onChange={(e) => handleOverlayFieldChange(col.key, e.target.value)}
                          placeholder="Image URL"
                        />
                        <button className="AdminIconUploadBtn" title="Upload" onClick={() => {
                          const input = document.createElement("input");
                          input.type = "file"; input.accept = "image/*";
                          input.onchange = (e) => {
                            const file = e.target.files?.[0];
                            if (!file) return;
                            const reader = new FileReader();
                            reader.onloadend = () => {
                              const dataUrl = reader.result;
                              handleOverlayFieldChange(col.key, dataUrl);
                              uploadToCloudinary(dataUrl, file.name)
                                .then(url => handleOverlayFieldChange(col.key, url))
                                .catch(err => setMessage("Upload failed: " + err.message));
                            };
                            reader.readAsDataURL(file);
                          };
                          input.click();
                        }}>
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><path d="M9 16h6v-6h4l-7-7-7 7h4zm-4 2h14v2H5z"/></svg>
                        </button>
                      </div>
                    </div>                      ) : col.key === "date" ? (
                    <input
                      type="text"
                      className="AdminInput"
                      value={toDDMMYYYY(editingEventData[col.key] || "")}
                      onChange={(e) => handleOverlayFieldChange(col.key, fromDDMMYYYY(e.target.value))}
                      placeholder="DD/MM/YYYY"
                    />
                  ) : col.type === "select" ? (
                    <select
                      className="AdminInput"
                      value={editingEventData[col.key] || ""}
                      onChange={(e) => handleOverlayFieldChange(col.key, e.target.value)}
                    >
                      {col.options.map((opt) => (
                        <option key={opt} value={opt}>{opt}</option>
                      ))}
                    </select>
                  ) : (
                    <input
                      className="AdminInput"
                      value={editingEventData[col.key] || ""}
                      onChange={(e) => handleOverlayFieldChange(col.key, e.target.value)}
                      placeholder={col.label}
                    />
                  )}
                </div>
              ))}
            </div>

            <div style={{ marginTop: 16, display: "flex", gap: 12, justifyContent: "center" }}>
              {editingEventMode === "edit" && editingEventIdx !== null && (
                <button className="AdminButton AdminButtonSmall" style={{ background: "#dc2626", borderColor: "#dc2626", color: "#fff" }}
                  onClick={() => { handleDeleteRow("featuredEvents", editingEventIdx); setEditingEventIdx(null); setEditingEventData(null); setCalendarEventOverlayMonth(null); }}>Delete</button>
              )}
              <button className="AdminButton AdminButtonSmall AdminSubmitBtn" onClick={handleSaveEventOverlay}>Save</button>
            </div>
          </div>
        </div>
      )}

      {activeTab === "calendar" && (
        <div className="AdminCard">
          <div className="AdminCardHeader">
            <h3 className="AdminFooterH3">Calendar View <span className="AdminCountBadge">{calendarGroups.length}</span></h3>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0, marginLeft: "auto" }}>
              <div className={`AdminPillSearchWrap ${calSearchOpen ? 'is-open' : ''}`}>
                <span className="AdminPillSearchIcon">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
                  </svg>
                </span>
                <input
                  ref={calSearchRef} type="text" className="AdminPillSearch" placeholder="Search..."
                  value={calSearch} onChange={(e) => setCalSearch(e.target.value)}
                  onBlur={() => { setCalSearch(""); setTimeout(() => setCalSearchOpen(false), 200) }}
                />
                {!calSearchOpen && (
                  <button className="AdminPillSearchOverlay" onClick={() => setCalSearchOpen(true)} type="button" aria-label="Search" />
                )}
              </div>
              <button className="AdminButton AdminButtonSmall AdminButtonSecondary" onClick={() => setShowAddMonth(true)}>
                + Add Month
              </button>
            </div>
          </div>
          <div className="AdminTableScroll">
            <table className="AdminTable AdminTableEditor">
              <thead>
                <tr>
                  <th className="AdminThOrder">Year</th>
                  <th>Month</th>
                  <th>Events</th>
                  <th className="AdminThActions">Actions</th>
                </tr>
              </thead>
              <tbody>
                {calendarGroups.filter(g => {
                  if (!calSearch.trim()) return true;
                  const q = calSearch.toLowerCase();
                  return String(g.year).includes(q) || g.monthLabel.toLowerCase().includes(q) || String(g.events.length).includes(q);
                }).map((g) => (
                  <tr key={`${g.year}-${g.month}`}>
                    <td style={{ fontWeight: 600 }}>{g.year}</td>
                    <td>{g.monthLabel}</td>
                    <td>{g.events.length}</td>
                    <td>
                      <button className="AdminIconBtn AdminIconBtnBlue" title="Edit month events" onClick={() => handleCalendarEdit(g.year, g.month)}>
                        &#9998;
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {calendarGroups.length === 0 && (
            <div className="AdminEmptyState">No events with valid dates found. Add events in the Featured Events tab.</div>
          )}
        </div>
      )}

      {calendarMonth !== null && (() => {
        const [calYear, calMonth] = calendarMonth.split("-").map(Number);
        const monthEvents = (content?.featuredEvents || [])
          .map((ev, idx) => ({ ...ev, _idx: idx }))
          .filter((ev) => {
            if (!ev.date) return false;
            const d = new Date(ev.date);
            return !isNaN(d.getTime()) && d.getFullYear() === calYear && d.getMonth() === calMonth;
          });
        return (
          <div className="AdminModal">
            <div className="AdminModalContent" style={{ maxWidth: 960, maxHeight: "85vh", overflowY: "auto" }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
                <h3 className="AdminModalTitle" style={{ margin: 0 }}>{MONTHS[calMonth]} {calYear} <span className="AdminCountBadge">{monthEvents.length}</span></h3>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  {monthEvents.length > 0 && (
                    <select
                      className="AdminInput"
                      style={{ fontSize: 13, padding: '6px 10px', maxWidth: 220 }}
                      value=""
                      onChange={(e) => {
                        const idx = parseInt(e.target.value, 10);
                        if (!isNaN(idx)) {
                          setCalendarEventOverlayMonth(calendarMonth);
                          openEventOverlay(idx);
                        }
                      }}
                    >
                      <option value="">Jump to event...</option>
                      {monthEvents.map((ev) => (
                        <option key={ev._idx} value={ev._idx}>
                          {ev.title || "Untitled"} — {ev.date || "—"}
                        </option>
                      ))}
                    </select>
                  )}
                  <button className="AdminCloseBtn" onClick={handleCloseCalendarEdit}>&times;</button>
                </div>
              </div>
              <div className="AdminCalEventGrid">
                {monthEvents.map((ev) => (
                  <div key={ev._idx} className="AdminCalEventCard" onClick={() => { setCalendarEventOverlayMonth(calendarMonth); openEventOverlay(ev._idx); }}>
                    <div className="AdminCalEventCardHeader">
                      {ev.imageUrl && (
                        <div className="AdminCalEventCardImgWrap">
                          <img src={ev.imageUrl} alt="" className="AdminCalEventCardImg" onError={(e) => { e.target.style.display = "none" }} />
                        </div>
                      )}
                      <div className="AdminCalEventCardMeta">
                        <h4 className="AdminCalEventCardTitle">{ev.title || "Untitled Event"}</h4>                          <span className="AdminCalEventCardDate">{formatDDMMYYYY(ev.date)}</span>
                      </div>
                    </div>
                    <div className="AdminCalEventCardBody">
                      {(ev.category || ev.country || ev.city) && (
                        <div className="AdminCalEventCardDetails">
                          {ev.category && <span className="AdminCalEventCardBadge">{ev.category}</span>}
                          {ev.country && <span className="AdminCalEventCardDetail"><svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><path d="M12 2a15 15 0 0 1 0 20 15 15 0 0 1 0-20z"/><line x1="2" y1="12" x2="22" y2="12"/></svg> {ev.country}</span>}
                          {ev.city && <span className="AdminCalEventCardDetail"><svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/></svg> {ev.city}</span>}
                        </div>
                      )}
                      {ev.description && (
                        <p className="AdminCalEventCardDesc">{ev.description}</p>
                      )}
                      {ev.visaRequired && (
                        <span className="AdminCalEventCardVisa" style={{
                          background: ev.visaRequired === "Visa Required" ? "rgba(240, 165, 0, 0.18)" : "rgba(60, 201, 122, 0.16)",
                          color: ev.visaRequired === "Visa Required" ? "#f0a500" : "#3cc97a",
                        }}>{ev.visaRequired}</span>
                      )}
                    </div>
                    <div className="AdminCalEventCardActions" onClick={(e) => e.stopPropagation()}>
                      <button className="AdminIconBtn AdminIconBtnBlue" title="Edit" onClick={() => { setCalendarEventOverlayMonth(calendarMonth); openEventOverlay(ev._idx); }}>&#9998; Edit</button>
                      <button className="AdminIconBtn AdminIconBtnRed" title="Delete" onClick={() => handleDeleteRow("featuredEvents", ev._idx)}>&#10005; Delete</button>
                    </div>
                  </div>
                ))}
              </div>
              {monthEvents.length === 0 && (
                <div className="AdminEmptyState">No events in this month.</div>
              )}
              <div style={{ marginTop: 12, display: "flex", gap: 8, justifyContent: "center" }}>
                <button className="AdminButton AdminButtonSmall AdminButtonSecondary" onClick={() => openCalendarNewEventOverlay(calYear, calMonth)}>
                  + Add Event
                </button>
                <button className="AdminButton AdminButtonSmall AdminSubmitBtn" onClick={handleCloseCalendarEdit}>
                  Done
                </button>
              </div>
            </div>
          </div>
        );
      })()}

      {showAddMonth && (
        <div className="AdminModal">
          <div className="AdminModalContent" style={{ maxWidth: 380 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
              <h3 className="AdminModalTitle" style={{ margin: 0 }}>Add Month</h3>
              <button className="AdminCloseBtn" onClick={() => setShowAddMonth(false)}>&times;</button>
            </div>
            <div className="AdminFormGroup">
              <label className="AdminLabel">Year</label>
              <input
                className="AdminInput"
                type="number"
                value={addMonthYear}
                onChange={(e) => setAddMonthYear(Number(e.target.value))}
                min={2020}
                max={2100}
              />
            </div>
            <div className="AdminFormGroup">
              <label className="AdminLabel">Month</label>
              <select
                className="AdminInput"
                value={addMonthVal}
                onChange={(e) => setAddMonthVal(Number(e.target.value))}
              >
                {MONTHS.map((name, idx) => (
                  <option key={idx} value={idx}>{name}</option>
                ))}
              </select>
            </div>
            <div style={{ marginTop: 16, display: "flex", justifyContent: "center" }}>
              <button className="AdminButton AdminSubmitBtn" onClick={handleAddMonth}>
                Add & Open
              </button>
            </div>
          </div>
        </div>
      )}

      {activeTab === "categories" && (
        renderArrayEditor("categories", EVENT_COLUMNS.categories, catSearch, catSearchOpen, setCatSearch, setCatSearchOpen, catSearchRef)
      )}



      {activeTab === "countries" && (
        <div className="AdminCardMb">
          <div className="AdminSectionHeader">
            <h4 className="AdminSectionTitle">Countries ({(content?.countries || countriesData).length})</h4>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
              <div className={`AdminPillSearchWrap ${countriesSearchOpen ? 'is-open' : ''}`}>
                <span className="AdminPillSearchIcon">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
                  </svg>
                </span>
                <input
                  ref={countrySearchRef} type="text" className="AdminPillSearch" placeholder="Search..."
                  value={countriesSearch} onChange={(e) => setCountriesSearch(e.target.value)}
                  onBlur={() => { setCountriesSearch(""); setTimeout(() => setCountriesSearchOpen(false), 200) }}
                />
                {!countriesSearchOpen && (
                  <button className="AdminPillSearchOverlay" onClick={() => setCountriesSearchOpen(true)} type="button" aria-label="Search" />
                )}
              </div>
              <button className="AdminButton AdminButtonSmall AdminButtonSecondary AdminAddRowBtn"
                onClick={() => {
                  setContent(prev => ({
                    ...prev,
                    countries: [...(prev.countries || countriesData), { name: "", region: "" }],
                  }));
                }}>
                + Add Row
              </button>
            </div>
          </div>
          <div className="AdminTableScroll">
            <table className="AdminTable AdminTableEditor">
              <thead>
                <tr>
                  <th className="AdminThOrder">Order</th>
                  <th>Name</th>
                  <th>Region</th>
                  <th className="AdminThActions">Actions</th>
                </tr>
              </thead>
              <tbody>
                {(content?.countries || countriesData)
                  .filter(c => {
                    if (!countriesSearch.trim()) return true;
                    const q = countriesSearch.toLowerCase();
                    return (c.name || "").toLowerCase().includes(q) || (c.region || "").toLowerCase().includes(q);
                  })
                  .map((c, idx) => (
                  <tr key={idx}
                    draggable="true"
                    onDragStart={(e) => { dragRef.current = { type: "countries", fromIndex: idx }; e.dataTransfer.effectAllowed = "move"; e.dataTransfer.setData("text/plain", ""); e.currentTarget.style.opacity = "0.4"; }}
                    onDragOver={(e) => { e.preventDefault(); e.dataTransfer.dropEffect = "move"; e.currentTarget.style.outline = "2px dashed #3b82f6"; e.currentTarget.style.outlineOffset = "-2px"; }}
                    onDragLeave={(e) => { e.currentTarget.style.outline = ""; }}
                    onDrop={(e) => {
                      e.preventDefault(); e.stopPropagation(); e.currentTarget.style.opacity = ""; e.currentTarget.style.outline = "";
                      if (!dragRef.current || dragRef.current.type !== "countries" || dragRef.current.fromIndex === idx) { dragRef.current = null; return; }
                      setContent(prev => { const arr = [...(prev.countries || countriesData)]; const [m] = arr.splice(dragRef.current.fromIndex, 1); arr.splice(idx, 0, m); return { ...prev, countries: arr }; });
                      dragRef.current = null;
                    }}
                    onDragEnd={(e) => { e.currentTarget.style.opacity = ""; e.currentTarget.style.outline = ""; dragRef.current = null; }}
                  >
                    <td>
                      <div className="AdminOrderBtns">
                        <button className="AdminMoveBtn" onClick={() => { if (idx === 0) return; setContent(prev => { const arr = [...(prev.countries || countriesData)]; [arr[idx-1], arr[idx]] = [arr[idx], arr[idx-1]]; return { ...prev, countries: arr }; }); }} disabled={idx === 0} title="Move up">&#8593;</button>
                        <button className="AdminMoveBtn" onClick={() => { const arr = [...(content?.countries || countriesData)]; if (idx >= arr.length - 1) return; setContent(prev => { const a = [...(prev.countries || countriesData)]; [a[idx], a[idx+1]] = [a[idx+1], a[idx]]; return { ...prev, countries: a }; }); }} disabled={idx >= (content?.countries || countriesData).length - 1} title="Move down">&#8595;</button>
                      </div>
                    </td>
                    <td>
                      <input className="AdminInput AdminTableInput" value={c.name || ""}
                        onChange={(e) => { const arr = [...(content?.countries || countriesData)]; arr[idx] = { ...arr[idx], name: e.target.value }; setContent(prev => ({ ...prev, countries: arr })); }}
                        placeholder="Name" />
                    </td>
                    <td>
                      <input className="AdminInput AdminTableInput" value={c.region || ""}
                        onChange={(e) => { const arr = [...(content?.countries || countriesData)]; arr[idx] = { ...arr[idx], region: e.target.value }; setContent(prev => ({ ...prev, countries: arr })); }}
                        placeholder="Region" style={{ width: 120 }} />
                    </td>
                    <td>
                      <div className="AdminActionBtns">
                        <button className="AdminIconBtn AdminIconBtnBlue" title="Add above" onClick={() => { const arr = [...(content?.countries || countriesData)]; arr.splice(idx, 0, { name: "", region: "" }); setContent(prev => ({ ...prev, countries: arr })); }}>&#9650;+</button>
                        <button className="AdminIconBtn AdminIconBtnBlue" title="Add below" onClick={() => { const arr = [...(content?.countries || countriesData)]; arr.splice(idx + 1, 0, { name: "", region: "" }); setContent(prev => ({ ...prev, countries: arr })); }}>&#9660;+</button>
                        <button className="AdminIconBtn AdminIconBtnRed" title="Delete" onClick={() => { const arr = [...(content?.countries || countriesData)]; arr.splice(idx, 1); setContent(prev => ({ ...prev, countries: arr })); }}>&#10005;</button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {(content?.countries || countriesData).length === 0 && (
            <div className="AdminEmptyState">{EVENT_MESSAGES.emptyArray}</div>
          )}
        </div>
      )}

      {showNoteModal && (
        <div className="AdminModal">
          <div className="AdminModalContent" style={{ maxWidth: 480 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
              <h3 className="AdminModalTitle" style={{ margin: 0 }}>Describe Your Changes</h3>
              <button className="AdminCloseBtn" onClick={() => setShowNoteModal(false)}>&times;</button>
            </div>
            <p style={{ fontSize: 13, color: "#64748b", margin: "0 0 12px 0" }}>
              Please provide a note describing what changes you made. This is required before submitting.
            </p>
            <textarea
              className="AdminTextarea"
              style={{ minHeight: 100, width: "100%", boxSizing: "border-box" }}
              placeholder="Describe what changed..."
              value={noteText}
              onChange={(e) => setNoteText(e.target.value)}
              autoFocus
            />
            <div style={{ marginTop: 16, display: "flex", justifyContent: "center" }}>
              <button className="AdminButton AdminSubmitBtn" onClick={handleConfirmSubmit} disabled={!noteText.trim() || saving}
                style={{ opacity: !noteText.trim() ? 0.5 : 1 }}>
                {saving ? "Submitting..." : "Continue"}
              </button>
            </div>
          </div>
        </div>
      )}

      {showSuccessOverlay && (
        <div className="AdminModal" onClick={() => setShowSuccessOverlay(false)}>
          <div className="AdminModalContent" style={{ maxWidth: 400, textAlign: "center" }} onClick={e => e.stopPropagation()}>
            <div style={{ fontSize: 48, marginBottom: 8 }}>
              {successTitle === "Changes Published" ? "\u2705" : "\uD83D\uDCEB"}
            </div>
            <h3 className="AdminModalTitle" style={{ marginBottom: 4 }}>{successTitle}</h3>
            <p style={{ fontSize: 13, color: "#64748b", margin: "0 0 20px 0" }}>
              {successTitle === "Changes Published" ? "Your content changes have been published live." : "Your change request has been submitted for approval."}
            </p>
            <div style={{ display: "flex", justifyContent: "center" }}>
              <button className="AdminButton AdminSubmitBtn" onClick={() => setShowSuccessOverlay(false)}>
                OK
              </button>
            </div>
          </div>
        </div>
      )}

      {logoEditorOpen && (
        <AdminLogoEditor
          currentImage={logoEditorImage}
          logoHistory={logoEditorRefKey ? (content?.imageHistory?.[logoEditorRefKey] || []) : []}
          logoBin={logoEditorRefKey ? (content?.imageBin?.[logoEditorRefKey] || []) : []}
          onSave={handleLogoEditorSave}
          onDeleteHistory={handleLogoEditorDeleteHistory}
          onRestoreFromBin={handleLogoEditorRestoreBin}
          onPermanentDelete={handleLogoEditorPermanentDelete}
          onClose={handleLogoEditorClose}
          accept={logoEditorAccept}
        />
      )}

      {showDuplicateModal && duplicateInfo.length > 0 && (
        <div className="AdminModal">
          <div className="AdminModalContent" style={{ maxWidth: 520, position: "relative" }}>
            <button onClick={() => setShowDuplicateModal(false)} style={{ position: "absolute", top: 8, right: 12, background: "none", border: "none", fontSize: 24, cursor: "pointer", color: "#64748b", lineHeight: 1 }} title="Close">&times;</button>
            <div style={{ fontSize: 36, textAlign: "center", marginBottom: 8 }}>&#9888;</div>
            <h3 className="AdminModalTitle" style={{ textAlign: "center", margin: "0 0 4px 0" }}>Duplicate Entries Detected</h3>
            <p style={{ fontSize: 13, color: "#64748b", textAlign: "center", margin: "0 0 16px 0" }}>
              The following duplicate entries were found. Choose how to proceed.
            </p>
            <div style={{ background: "#fff8f0", border: "1px solid #fecaca", borderRadius: 8, padding: 12, marginBottom: 16 }}>
              {(() => {
                const byTab = {};
                for (const d of duplicateInfo) {
                  if (!byTab[d.tabId]) byTab[d.tabId] = { label: d.tabLabel, items: [] };
                  byTab[d.tabId].items.push(d.duplicateValue);
                }
                return Object.entries(byTab).map(([tabId, info]) => (
                  <div key={tabId} style={{ marginBottom: 8, fontSize: 14, textAlign: "center" }}>
                    <strong style={{ color: "#1B2A4A" }}>{info.label}</strong>
                    <span style={{ color: "#475569" }}> &mdash; Duplicate{info.items.length > 1 ? "s" : ""}: </span>
                    <span style={{ color: "#1B2A4A" }}>{info.items.join(", ")}</span>
                  </div>
                ));
              })()}
            </div>
            <div style={{ display: "flex", gap: 12, justifyContent: "center" }}>
              <button className="AdminButton AdminSubmitBtn" onClick={handleRemoveDuplicates} disabled={duplicateActionLoading}
                style={{ background: "#dc2626", borderColor: "#dc2626" }}>
                {duplicateActionLoading ? "Removing..." : "Remove Duplicate Rows"}
              </button>
              <button className="AdminButton AdminSubmitBtn" onClick={handleContinueAnyway} disabled={duplicateActionLoading}>
                Continue Anyway
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}

export default AdminEventEditor;
